/**
 * ChatGlobal.tsx — Chat global flutuante do vault BAM
 *
 * Flutua no canto inferior direito da tela.
 * Envia perguntas para a Cloud Function /api/chat (Gemini 2.5 Pro + RAG).
 * Recebe cited_node_ids e dispara evento "node-glow" para o Graph3D.
 */

import { useState, useRef, useEffect, type FormEvent } from 'react'
import { auth } from '../lib/firebase'

interface Message {
  id: string
  role: 'user' | 'assistant'
  content: string
  cited_node_ids?: string[]
  loading?: boolean
}

interface ChatResponse {
  answer: string
  cited_node_ids: string[]
  model_used: string
}

interface ChatGlobalProps {
  /** Callback para notificar o componente pai quais nós foram citados */
  onCitedNodes?: (nodeIds: string[]) => void
  /** Quando setado, o chat abre automaticamente e fica focado nesta nota */
  focusNoteId?: string | null
  /** Título da nota em foco (para exibir no header) */
  focusNoteTitle?: string | null
  /** Callback para limpar o foco no pai */
  onClearFocus?: () => void
}

export function ChatGlobal({ onCitedNodes, focusNoteId, focusNoteTitle, onClearFocus }: ChatGlobalProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [messages, setMessages] = useState<Message[]>([])
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)

  // Scroll automático ao receber nova mensagem
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  const sendMessage = async (e: FormEvent) => {
    e.preventDefault()
    const question = input.trim()
    if (!question || loading) return

    const userMsg: Message = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: question,
    }

    const loadingMsg: Message = {
      id: `assistant-loading-${Date.now()}`,
      role: 'assistant',
      content: '',
      loading: true,
    }

    setMessages((prev) => [...prev, userMsg, loadingMsg])
    setInput('')
    setLoading(true)

    try {
      // Token Firebase Auth
      const token = await auth.currentUser?.getIdToken()
      if (!token) {
        throw new Error('Sessão expirada — faça login novamente')
      }

      // Chama Cloud Function via rewrite Hosting (/api/chat → chat function)
      const response = await fetch('/api/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          question,
          focus_note_id: focusNoteId ?? undefined,
          // Envia histórico recente (últimas 6 mensagens) para contexto
          history: messages.slice(-6).map((m) => ({
            role: m.role === 'user' ? 'user' : 'model',
            parts: [{ text: m.content }],
          })),
        }),
      })

      if (!response.ok) {
        const errText = await response.text()
        let errMsg = `${response.status}`
        try {
          const j = JSON.parse(errText)
          if (j.error) errMsg = j.error
        } catch {
          if (errText) errMsg = errText.slice(0, 200)
        }
        throw new Error(errMsg)
      }

      const data: ChatResponse = await response.json()

      // Substitui mensagem de loading pela resposta real
      setMessages((prev) =>
        prev.map((m) =>
          m.loading
            ? {
                id: `assistant-${Date.now()}`,
                role: 'assistant',
                content: data.answer,
                cited_node_ids: data.cited_node_ids,
                loading: false,
              }
            : m
        )
      )

      // Notifica quais nós foram citados (para glow no grafo)
      if (data.cited_node_ids?.length > 0) {
        onCitedNodes?.(data.cited_node_ids)

        // Dispara evento DOM para integração com Graph3D
        window.dispatchEvent(
          new CustomEvent('node-glow', {
            detail: { nodeIds: data.cited_node_ids },
          })
        )
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro desconhecido'
      setMessages((prev) =>
        prev.map((m) =>
          m.loading
            ? {
                id: `error-${Date.now()}`,
                role: 'assistant',
                content: `Erro ao consultar o vault: ${errorMessage}`,
                loading: false,
              }
            : m
        )
      )
    } finally {
      setLoading(false)
    }
  }

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 'var(--chat-bottom, 24px)',
        right: 'var(--chat-right, 24px)',
        left: 'var(--chat-left, auto)',
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        gap: '12px',
      }}
    >
      {/* Painel de chat */}
      {isOpen && (
        <div
          style={{
            width: 'var(--chat-w, 380px)',
            height: 'var(--chat-h, 500px)',
            maxWidth: 'calc(100vw - 24px)',
            maxHeight: 'calc(100vh - 100px)',
            background: 'rgba(13, 13, 30, 0.95)',
            border: '1px solid rgba(87, 245, 195, 0.2)',
            borderRadius: '12px',
            display: 'flex',
            flexDirection: 'column',
            backdropFilter: 'blur(20px)',
            boxShadow: '0 0 40px rgba(87, 245, 195, 0.1), 0 20px 60px rgba(0,0,0,0.5)',
          }}
        >
          {/* Header */}
          <div
            style={{
              padding: '14px 16px',
              borderBottom: '1px solid rgba(255,255,255,0.06)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: '13px', fontWeight: 600, color: '#57f5c3' }}>
                Vault BAM
              </div>
              <div style={{ fontSize: '11px', color: '#5a5a80', marginTop: '2px' }}>
                Gemini 2.5 Pro · RAG
              </div>
            </div>
            <button
              onClick={() => setMessages([])}
              style={{
                background: 'none',
                border: 'none',
                color: '#5a5a80',
                cursor: 'pointer',
                fontSize: '11px',
              }}
              title="Limpar conversa"
            >
              limpar
            </button>
          </div>

          {/* Pílula de foco */}
          {focusNoteId && (
            <div
              style={{
                margin: '10px 16px 0',
                padding: '6px 10px',
                background: 'rgba(87, 245, 195, 0.08)',
                border: '1px solid rgba(87, 245, 195, 0.25)',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '11px',
                color: '#57f5c3',
              }}
            >
              <span style={{ opacity: 0.7, flexShrink: 0 }}>focado em:</span>
              <span
                style={{
                  flex: 1,
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  fontWeight: 600,
                }}
                title={focusNoteTitle ?? focusNoteId}
              >
                {focusNoteTitle ?? focusNoteId}
              </span>
              {onClearFocus && (
                <button
                  onClick={onClearFocus}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#57f5c3',
                    cursor: 'pointer',
                    fontSize: '13px',
                    padding: 0,
                    lineHeight: 1,
                    opacity: 0.7,
                  }}
                  title="Remover foco"
                >
                  ×
                </button>
              )}
            </div>
          )}

          {/* Mensagens */}
          <div
            style={{
              flex: 1,
              overflowY: 'auto',
              padding: '16px',
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
            }}
          >
            {messages.length === 0 && (
              <div
                style={{
                  color: '#5a5a80',
                  fontSize: '13px',
                  textAlign: 'center',
                  marginTop: '60px',
                  lineHeight: 1.6,
                }}
              >
                Pergunte qualquer coisa sobre o vault da BAM.
                <br />
                <span style={{ fontSize: '11px' }}>
                  Frameworks, clientes, projetos, processos...
                </span>
              </div>
            )}

            {messages.map((msg) => (
              <div
                key={msg.id}
                style={{
                  alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start',
                  maxWidth: '85%',
                }}
              >
                <div
                  style={{
                    padding: '8px 12px',
                    borderRadius: msg.role === 'user' ? '12px 12px 4px 12px' : '12px 12px 12px 4px',
                    background:
                      msg.role === 'user'
                        ? 'rgba(87, 245, 195, 0.15)'
                        : 'rgba(255,255,255,0.05)',
                    border:
                      msg.role === 'user'
                        ? '1px solid rgba(87, 245, 195, 0.3)'
                        : '1px solid rgba(255,255,255,0.06)',
                    fontSize: '13px',
                    lineHeight: 1.5,
                    color: msg.loading ? '#5a5a80' : '#e8e8f0',
                  }}
                >
                  {msg.loading ? '...' : msg.content}
                </div>

                {/* IDs de nós citados (debug / link) */}
                {msg.cited_node_ids && msg.cited_node_ids.length > 0 && (
                  <div
                    style={{
                      marginTop: '4px',
                      fontSize: '10px',
                      color: '#57f5c3',
                      opacity: 0.6,
                    }}
                  >
                    {msg.cited_node_ids.length} fonte(s) citada(s)
                  </div>
                )}
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <form
            onSubmit={sendMessage}
            style={{
              padding: '12px 16px',
              borderTop: '1px solid rgba(255,255,255,0.06)',
              display: 'flex',
              gap: '8px',
            }}
          >
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={focusNoteId ? 'Pergunte sobre esta nota...' : 'Pergunte ao vault...'}
              disabled={loading}
              style={{ flex: 1 }}
              autoFocus
            />
            <button
              type="submit"
              disabled={loading || !input.trim()}
              className="btn btn-primary"
              style={{ padding: '6px 12px', flexShrink: 0 }}
            >
              →
            </button>
          </form>
        </div>
      )}

      {/* Botão flutuante */}
      <button
        onClick={() => setIsOpen((v) => !v)}
        style={{
          width: 'var(--chat-fab, 52px)',
          height: 'var(--chat-fab, 52px)',
          borderRadius: '50%',
          background: isOpen
            ? 'rgba(87, 245, 195, 0.3)'
            : 'rgba(13, 13, 30, 0.9)',
          border: '1px solid rgba(87, 245, 195, 0.4)',
          color: '#57f5c3',
          fontSize: '22px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: isOpen
            ? '0 0 30px rgba(87, 245, 195, 0.4)'
            : '0 0 15px rgba(87, 245, 195, 0.15)',
          transition: 'all 250ms ease',
          backdropFilter: 'blur(10px)',
        }}
        title={isOpen ? 'Fechar chat' : 'Abrir chat do vault'}
      >
        {isOpen ? '×' : '✦'}
      </button>
    </div>
  )
}
